import { useState, useEffect } from 'react';
import { useFormik } from "formik";
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import {
    SlDrawer, SlTooltip,
    SlIconButton, SlButton
} from '@shoelace-style/shoelace/dist/react';
import ScrollContainer from 'react-indiana-drag-scroll';

import api from "../../services/api";
import { formatDateWithTime } from '../../utils/dateFormat';
import { Toast } from '../../shared/Toast';
import NoDataFound from '../../shared/NoDataFound';
import TableLoader from '../../shared/TableLoader';
import Paginator from '../../shared/Paginator';
import config from '../../config';
import { languageOptions } from '../../utils/constants';
import PendingRequestModal from '../MakerChecker/components/PendingRequestModal';
import TableSkelton from '../../shared/TableSkelton';

const initialClassified = {
    name: '',
    email: '',
    mobile: '',
    password: '',
    isagency: false,
    language_id: '',
    state_id: '',
    district_id: '',
    address: '',
    status:1
};

const ClassifiedSchema = Yup.object().shape({
    name: Yup.string()
        .matches(/^[a-zA-Z][a-zA-Z0-9_ ]*$/, "Must start with a letter; only letters, numbers, spaces, and '_' allowed")
        .required('Name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    mobile: Yup.string()
        .matches(/^[6-9]\d{9}$/, "Invalid Mobile number")
        .required("Mobile Number is required"),
    password: Yup.string()
        .matches(/^[a-zA-Z0-9][a-zA-Z0-9./!@#$%^&*\-?+~]*$/, "Password should not contain white space"),
    isagency: Yup.boolean(),
    language_id: Yup.string().required("Language is required"),
    state_id: Yup.string().required("State is required"),
    district_id: Yup.string().required("District is required"),
    address: Yup.string().optional(),
});

function Classifieds() {
    const navigate = useNavigate();
    const HeaderSearch = useSelector(state => state.Head.adssearch);

    const [data, setData] = useState([]);
    const [show, setShow] = useState(false);
    const [loader, setLoader] = useState(false);
    const [submit, setSubmit] = useState(false);
    const [flag, setFlag] = useState('');
    const [id, setId] = useState('');
    const [search, setSearch] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);

    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [districtsMap, setDistrictsMap] = useState({});

    const [selectedMcId, setSelectedMcId] = useState('');

    const handleClose = () => {
        setShow(false);
        setSubmit(false);
        setDistricts([]);
        formik.resetForm();
    };

    const handleCloseModal = () => {
        setSelectedMcId(null);
    };

    const handleShow = (type, item = {}) => {
        if (type === "add") {
            formik.setValues(initialClassified);
            setDistricts([]);
        } else if (type === "edit") {
            formik.setValues({
                name: item.name,
                email: item.email,
                mobile: item.mobile,
                password: '',
                isagency: !!item.isagency,
                language_id: item.language_id || '',
                state_id: item.state_id || '',
                district_id: item.district_id || '',
                address: item.address || '',
                status: +item.status,
            });
            setId(item.user_id);
            if (item.state_id) getDistricts(item.state_id);
        }
        setFlag(type);
        setShow(true);
    };

    const handlePageClick = (event) => {
        setCurrentPage(event.selected);
        loadData(search, event.selected);
    };

    const loadData = async (searchString = '', page = 0) => {
        try {
            setLoader(true);
            const res = await api({
                url: `/classified_user/list`,
                method: "POST",
                data: { page, limit: config.PER_PAGE, search: searchString },
            });
            const rows = res.data.data || [];
            setData(rows);
            setTotalPages(Math.ceil(res.data.total / config.PER_PAGE));
            setLoader(false);

            // Build districts lookup map for all unique state_ids in the result
            const uniqueStateIds = [...new Set(rows.map(r => r.state_id).filter(Boolean))];
            if (uniqueStateIds.length) {
                const results = await Promise.all(
                    uniqueStateIds.map(state_id =>
                        api({ url: `/user/company/getdistricts`, method: "POST", data: { state_id } })
                    )
                );
                const map = {};
                results.forEach(r => {
                    if (r.code === 200 && Array.isArray(r.data)) {
                        r.data.forEach(d => { map[d.dist_id] = d.dist_name; });
                    }
                });
                setDistrictsMap(prev => ({ ...prev, ...map }));
            }
        } catch (error) {
            setLoader(false);
            if (error.response?.status === 401) navigate("/");
        }
    };

    const getStates = async () => {
        try {
            const res = await api({
                url: `/user/company/getstates`,
                method: "GET",
            });
            if (res?.data?.data) setStates(res.data.data);
        } catch (error) {
            console.log(error);
        }
    };

    const getDistricts = async (state_id) => {
        try {
            const res = await api({
                url: `/user/company/getdistricts`,
                method: "POST",
                data: { state_id },
            });
            if (res.code === 200) setDistricts(res.data);
            else setDistricts([]);
        } catch (error) {
            console.log(error);
        }
    };

    const handleStateChange = (e) => {
        const state_id = e.target.value;
        formik.setFieldValue('state_id', state_id);
        formik.setFieldValue('district_id', '');
        setDistricts([]);
        if (state_id) getDistricts(state_id);
    };

    const Header = () => {
        setSearch(HeaderSearch);
        setCurrentPage(0);
        loadData(HeaderSearch, 0);
    };

    useEffect(() => {
        Header();
        getStates();
    }, [HeaderSearch]);

    const handleRequestClose = (event) => {
        if (event.detail.source === 'overlay') event.preventDefault();
    };

    const formik = useFormik({
        initialValues: initialClassified,
        validationSchema: ClassifiedSchema,
        onSubmit: async (values) => {
            try {
                const { name, email, mobile, password, isagency, language_id, state_id, district_id, address, status } = values;
                const payload = flag === 'edit'
                    ? { name, email, mobile, isagency, language_id, state_id, district_id, address, status: +status,ref_table:'classified_users' }
                    : { name, email, mobile, password, isagency, language_id, state_id, district_id, status, address,ref_table:'classified_users' };

                const url = flag === 'add' ? `/classified_user/create` : `/classified_user/edit/${id}`;

                const res = await api({
                    url,
                    method: "POST",
                    data: { ...payload, ref_table: 'classified_users' },
                });
                if (res.code !== 200) {
                    Toast("error", res.data?.message || res.message);
                } else {
                    Toast("success", `Classified User ${flag === 'add' ? 'created' : 'updated'} request sent successfully`);
                    handleClose();
                    loadData(search, currentPage);
                }
            } catch (error) {
                Toast("error", error?.response?.message || "Something went wrong");
                if (error.response?.status === 401) navigate("/");
            }
        }
    });

    const getLanguageLabel = (langId) => {
        const found = languageOptions.find(l => l.value === +langId);
        return found ? found.label : langId;
    };

    const getStateName = (state_id) => {
        const found = states.find(s => +s.state_id === +state_id);
        return found ? found.state_name : state_id;
    };

    const getDistrictName = (dist_id) => districtsMap[dist_id] || dist_id;

    return (
        <>
            <div className='flex gap-4 justify-end mb-4'>
                <SlButton size="small" variant="primary" onClick={() => handleShow("add")}>Add Classified User</SlButton>
            </div>

            <SlDrawer label={`${flag === 'edit' ? 'Edit' : 'Create'} Classified User`} open={show} onSlRequestClose={handleRequestClose} onSlAfterHide={handleClose}>
                <form onSubmit={formik.handleSubmit}>
                    {/* Name */}
                    <div className="mb-3">
                        <label className="mb-1">Name</label><span className="text-red-600">*</span>
                        <input
                            type="text"
                            className="form-control"
                            name="name"
                            placeholder="Enter name"
                            value={formik.values.name}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                        />
                        {submit && formik.errors.name && <div className="text-red-600">{formik.errors.name}</div>}
                    </div>

                    {/* Email */}
                    <div className="mb-3">
                        <label className="mb-1">Email</label><span className="text-red-600">*</span>
                        <input
                            type="email"
                            className="form-control"
                            name="email"
                            placeholder="Enter email"
                            value={formik.values.email}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                        />
                        {submit && formik.errors.email && <div className="text-red-600">{formik.errors.email}</div>}
                    </div>

                    {/* Mobile */}
                    <div className="mb-3">
                        <label className="mb-1">Mobile</label><span className="text-red-600">*</span>
                        <input
                            type="text"
                            className="form-control"
                            name="mobile"
                            placeholder="Enter mobile"
                            value={formik.values.mobile}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                        />
                        {submit && formik.errors.mobile && <div className="text-red-600">{formik.errors.mobile}</div>}
                    </div>

                    {/* Password (add only) */}
                    {flag === 'add' && (
                        <div className="mb-3">
                            <label className="mb-1">Password</label><span className="text-red-600">*</span>
                            <input
                                type="text"
                                className="form-control"
                                name="password"
                                placeholder="Enter password"
                                value={formik.values.password}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                            />
                            {submit && formik.errors.password && <div className="text-red-600">{formik.errors.password}</div>}
                        </div>
                    )}

                    {/* Agency or Personal */}
                    <div className="mb-3">
                        <label className="mb-1 d-block">Type</label>
                        <div className="d-flex gap-3">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    id="typePersonal"
                                    checked={!formik.values.isagency}
                                    onChange={() => formik.setFieldValue('isagency', false)}
                                />
                                <label className="form-check-label" htmlFor="typePersonal">Personal</label>
                            </div>
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    id="typeAgency"
                                    checked={!!formik.values.isagency}
                                    onChange={() => formik.setFieldValue('isagency', true)}
                                />
                                <label className="form-check-label" htmlFor="typeAgency">Agency</label>
                            </div>
                        </div>
                    </div>

                    {/* Language */}
                    <div className="mb-3">
                        <label className="mb-1">Language</label><span className="text-red-600">*</span>
                        <select
                            className="form-control"
                            name="language_id"
                            value={formik.values.language_id}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                        >
                            <option value="">Select Language</option>
                            {languageOptions.map(lang => (
                                <option key={lang.value} value={lang.value}>{lang.label}</option>
                            ))}
                        </select>
                        {submit && formik.errors.language_id && <div className="text-red-600">{formik.errors.language_id}</div>}
                    </div>

                    {/* State */}
                    <div className="mb-3">
                        <label className="mb-1">State</label><span className="text-red-600">*</span>
                        <select
                            className="form-control"
                            name="state_id"
                            value={formik.values.state_id}
                            onBlur={formik.handleBlur}
                            onChange={handleStateChange}
                        >
                            <option value="">Select State</option>
                            {states.map(state => (
                                <option key={state.state_id} value={state.state_id}>{state.state_name}</option>
                            ))}
                        </select>
                        {submit && formik.errors.state_id && <div className="text-red-600">{formik.errors.state_id}</div>}
                    </div>

                    {/* District */}
                    <div className="mb-3">
                        <label className="mb-1">District</label><span className="text-red-600">*</span>
                        <select
                            className="form-control"
                            name="district_id"
                            value={formik.values.district_id}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            disabled={!formik.values.state_id}
                        >
                            <option value="">Select District</option>
                            {districts.map(dist => (
                                <option key={dist.dist_id} value={dist.dist_id}>{dist.dist_name}</option>
                            ))}
                        </select>
                        {submit && formik.errors.district_id && <div className="text-red-600">{formik.errors.district_id}</div>}
                    </div>

                    {/* Address */}
                    <div className="mb-3">
                        <label className="mb-1">Address</label>
                        <textarea
                            className="form-control"
                            name="address"
                            placeholder="Enter address"
                            rows={3}
                            value={formik.values.address}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                        />
                    </div>

                    {/* Status (edit only) */}
                    {flag === 'edit' && (
                        <div className="form-check form-switch mb-3">
                            <label className="form-check-label">Status</label>
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={!!formik.values.status}
                                onChange={(e) => formik.setFieldValue('status', e.target.checked)}
                            />
                        </div>
                    )}

                    <div slot="footer">
                        <SlButton type="submit" variant="primary" size="small" onClick={() => setSubmit(true)}>
                            Submit
                        </SlButton>
                    </div>
                </form>
            </SlDrawer>

            <div className='mb-4'>
                <ScrollContainer>
                    <table className='table table-bordered'>
                        <thead className="sticky top-0">
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Mobile</th>
                                <th>Type</th>
                                <th>Language</th>
                                <th>State</th>
                                <th>District</th>
                                <th>Address</th>
                                <th>Status</th>
                                <th>Created On</th>
                                <th>Created By</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        {(data.length && !loader) ? (
                            <tbody>
                                {data.map((item) => (
                                    <tr key={item.user_id}>
                                        <th scope="row" className="flex items-center justify-center text-center gap-2">
                                            {item?.mc_actions?.length
                                                ? <button className="h-2 w-2 px-1 py-1 rounded-full bg-orange-300" title={`pending edit action by ${item?.mc_actions?.[0]?.mc_user_name}`} onClick={() => setSelectedMcId(item?.mc_actions?.[0]?.mc_id)}></button>
                                                : null}
                                            <span>{item.name}</span>
                                        </th>
                                        <td>{item.email}</td>
                                        <td>{item.mobile}</td>
                                        <td>{item.isagency ? 'Agency' : 'Personal'}</td>
                                        <td>{getLanguageLabel(item.language_id)}</td>
                                        <td>{getStateName(item.state_id)}</td>
                                        <td>{getDistrictName(item.district_id)}</td>
                                        <td>{item.address || '-'}</td>
                                        <td>
                                            <span className={item.status ? "full-badge text-center" : "unpaid-badge text-center"}>
                                                {item.status ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td>{formatDateWithTime(item.created_at)}</td>
                                        <td>{item.b2b_name || '-'}</td>
                                        <td>
                                            <SlTooltip content="Edit Classified User">
                                                <SlIconButton
                                                    library="lucide"
                                                    name="edit"
                                                    onClick={() => handleShow("edit", item)}
                                                />
                                            </SlTooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        ) : !data.length && !loader ? (
                            <NoDataFound colspan="12" />
                        ) : (
                            <TableSkelton colspan={12} />
                        )}
                    </table>
                </ScrollContainer>
                {totalPages > 1 && (
                    <Paginator pageCount={totalPages} onPageChange={handlePageClick} />
                )}
                <PendingRequestModal mc_id={selectedMcId} isOpen={!!selectedMcId} onClose={handleCloseModal} />
            </div>
        </>
    );
}

export default Classifieds;
